import { useEffect, useState, useCallback, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const keyFor = (b) => `${b.unit_id || 'na'}::${b.url}`;

export function useLinkHealth(token) {
  const [run, setRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);
  const [batchFixing, setBatchFixing] = useState(false);
  const [reverifying, setReverifying] = useState(false);
  const [scheduleInfo, setScheduleInfo] = useState(null);
  /** suggestions: { [`${unit_id}::${url}`]: { loading, applied, suggestion, validation, error } } */
  const [suggestions, setSuggestions] = useState({});

  const authHeaders = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);

  const fetchLatest = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/link-health/latest`, authHeaders);
      setRun(res.data?.status === 'none' ? null : res.data);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message;
      toast.error(`Failed to load link health: ${msg}`);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { fetchLatest(); }, [fetchLatest]);

  useEffect(() => {
    const loadSchedule = async () => {
      try {
        const res = await axios.get(`${API}/admin/link-health/scheduled-runs`, authHeaders);
        setScheduleInfo(res.data);
      } catch (e) { console.warn('Link health: count fetch failed', e); }
    };
    if (token) loadSchedule();
  }, [token, authHeaders]);

  // Poll every 4s when a run is in progress
  useEffect(() => {
    if (!run || run.status !== 'running') {
      setPolling(false);
      return undefined;
    }
    setPolling(true);
    const id = setInterval(fetchLatest, 4000);
    return () => clearInterval(id);
  }, [run, fetchLatest]);

  const triggerRun = async () => {
    try {
      const res = await axios.post(`${API}/admin/link-health/run`, {}, authHeaders);
      toast.success(res.data?.message || 'Link check started — usually under a minute');
      setRun({ status: 'running', id: res.data.id, started_at: new Date().toISOString() });
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message;
      toast.error(`Failed to start run: ${msg}`);
    }
  };

  const suggestFix = async (b) => {
    const key = keyFor(b);
    setSuggestions((prev) => ({ ...prev, [key]: { loading: true } }));
    try {
      const res = await axios.post(
        `${API}/admin/link-health/suggest-fix`,
        {
          kind: b.kind,
          original_url: b.url,
          source_title: b.source_title || '',
          unit_title: b.unit_title || '',
        },
        authHeaders,
      );
      setSuggestions((prev) => ({ ...prev, [key]: { loading: false, ...res.data } }));
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message;
      setSuggestions((prev) => ({ ...prev, [key]: { loading: false, error: msg } }));
      toast.error(`AI suggestion failed: ${msg}`);
    }
  };

  const applyFix = async (b) => {
    const key = keyFor(b);
    const s = suggestions[key]?.suggestion;
    if (!s?.url) return;
    try {
      await axios.post(
        `${API}/admin/link-health/apply-fix`,
        {
          unit_id: b.unit_id,
          kind: b.kind,
          original_url: b.url,
          new_url: s.url,
          new_source_org: s.source_organization || '',
        },
        authHeaders,
      );
      setSuggestions((prev) => ({ ...prev, [key]: { ...prev[key], applied: true } }));
      // Optimistically remove from broken list
      setRun((prev) => prev && {
        ...prev,
        broken: (prev.broken || []).filter((x) => keyFor(x) !== key),
        summary: { ...prev.summary, broken: Math.max(0, (prev.summary?.broken ?? 1) - 1), ok: (prev.summary?.ok ?? 0) + 1 },
      });
      toast.success('Link updated');
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message;
      toast.error(`Apply failed: ${msg}`);
    }
  };

  const dismissSuggestion = (b) => {
    const key = keyFor(b);
    setSuggestions((prev) => {
      const copy = { ...prev };
      delete copy[key];
      return copy;
    });
  };

  const runBatchFix = async () => {
    const brokenCount = run?.summary?.broken ?? 0;
    if (brokenCount === 0) {
      toast.info('No broken URLs to fix');
      return;
    }
    if (!window.confirm(`Run Fix with AI on all ${brokenCount} broken URLs? This may take ~${Math.ceil(brokenCount * 2)}s.`)) return;
    setBatchFixing(true);
    try {
      const res = await axios.post(`${API}/admin/link-health/batch-fix`, {}, authHeaders);
      const { applied, skipped, errors, total } = res.data;
      toast.success(`Batch fix complete: ${applied}/${total} applied, ${skipped} unverified, ${errors} errors`);
      await fetchLatest();
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message;
      toast.error(`Batch fix failed: ${msg}`);
    } finally {
      setBatchFixing(false);
    }
  };

  const runReverify = async () => {
    const brokenCount = run?.summary?.broken ?? 0;
    if (brokenCount === 0) {
      toast.info('No broken URLs to re-verify');
      return;
    }
    if (!window.confirm(`Re-check all ${brokenCount} still-broken URLs with a longer 30 s timeout? URLs that now respond OK will be removed from the list.`)) return;
    setReverifying(true);
    try {
      const res = await axios.post(`${API}/admin/link-health/reverify`, {}, authHeaders);
      const { checked, recovered, still_broken } = res.data;
      toast.success(`Re-verify: ${recovered}/${checked} recovered, ${still_broken} still broken`);
      await fetchLatest();
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message;
      toast.error(`Re-verify failed: ${msg}`);
    } finally {
      setReverifying(false);
    }
  };

  const sendTestEmail = async () => {
    try {
      const res = await axios.post(`${API}/admin/link-health/send-test-email`, {}, authHeaders);
      toast.success(`Test email sent to ${res.data.sent_to.join(', ')}`);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message;
      toast.error(`Email failed: ${msg}`);
    }
  };

  return {
    run, loading, polling, batchFixing, reverifying, scheduleInfo, suggestions,
    triggerRun, suggestFix, applyFix, dismissSuggestion, runBatchFix, runReverify, sendTestEmail,
  };
}
